import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { money } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";

export const linesTotal = (lines) => lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0);
export const emptyItem = () => ({ description: "", qty: 1, rate: 0 });

export default function LineItems({ lines, onChange, quoted, onQuoted, saveOnBlur = false, testid = "line-items" }) {
  const [qv, setQv] = useState(quoted);
  useEffect(() => { setQv(quoted); }, [quoted]);
  const typeQ = (v) => { setQv(v); if (!saveOnBlur) onQuoted(v); };
  const commitQ = () => { if (saveOnBlur && Number(qv) !== Number(quoted)) onQuoted(qv); };
  const setLine = (i, k, v) => onChange(lines.map((l, x) => (x === i ? { ...l, [k]: v } : l)));
  const total = linesTotal(lines);
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wide">Line Items</Label>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...lines, emptyItem()])} data-testid={`${testid}-add`}><Plus className="w-3 h-3 mr-1" />Add item</Button>
      </div>
      {lines.length === 0 ? <p className="text-xs text-muted-foreground">No items yet. Add the parts or work being quoted, with quantities and prices.</p> : (
        <div className="space-y-2">
          <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] font-mono uppercase text-muted-foreground"><span className="col-span-5">Description</span><span className="col-span-2">Qty</span><span className="col-span-2">Unit price €</span><span className="col-span-2 text-right">Line total</span></div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`${testid}-row-${i}`}>
              <Input className="col-span-12 sm:col-span-5" placeholder="Description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`${testid}-desc-${i}`} />
              <Input className="col-span-4 sm:col-span-2" type="number" min="0" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`${testid}-qty-${i}`} />
              <Input className="col-span-4 sm:col-span-2" type="number" min="0" step="0.01" placeholder="€" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} data-testid={`${testid}-rate-${i}`} />
              <span className="col-span-3 sm:col-span-2 text-right font-mono-num text-sm" data-testid={`${testid}-amount-${i}`}>{money(Number(l.qty || 0) * Number(l.rate || 0))}</span>
              <button type="button" onClick={() => onChange(lines.filter((_, x) => x !== i))} className="col-span-1 justify-self-end text-muted-foreground hover:text-red-400 transition-colors" data-testid={`${testid}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-3 mt-3 pt-2 border-t border-border text-sm">
        <span>Items total: <b className="font-mono-num" data-testid={`${testid}-total`}>{money(total)}</b></span>
        {onQuoted && (
          <div className="flex items-center gap-2">
            <Label className="text-xs">Quoted Value (€)</Label>
            <Input type="number" value={qv} onChange={(e) => typeQ(e.target.value)} onBlur={commitQ} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} className="w-32" data-testid={`${testid}-quoted`} />
            {lines.length > 0 && Number(qv) !== Math.round(total * 100) / 100 && <Button type="button" size="sm" variant="ghost" onClick={() => { setQv(Math.round(total * 100) / 100); onQuoted(Math.round(total * 100) / 100); }} data-testid={`${testid}-use-total`}>Use items total</Button>}
          </div>
        )}
      </div>
    </div>
  );
}
